import { BACKEND_API_URL, BackendError } from "data/clients";
import { z } from "zod";

const responseSchema = z.object({ isAdmin: z.boolean() });

/**
 * Whether `email` belongs to a GPU-loans admin.
 *
 * Hand-written rather than routed through `backendClient` because the endpoint is
 * excluded from the backend's OpenAPI document, so codegen cannot type it. The zod
 * parse replaces the generated types it would otherwise rely on.
 *
 * Cached for 30 minutes under the "admins" tag. The cache key varies by email, and
 * the backend's `update-constants` webhook clears every entry at once through
 * `revalidateTag("admins")` — tags are invalidation groups, not cache keys.
 *
 * NOT an authorization boundary. The endpoint is public and trusts whatever email
 * it is handed, so callers must resolve `email` from a verified Privy token.
 */
export async function getIsAdmin(email: string): Promise<boolean> {
  if (!BACKEND_API_URL) {
    throw new BackendError("GET_IS_ADMIN_FAILED", 500, "NEXT_PUBLIC_BACKEND_API_URL is not set");
  }

  const query = new URLSearchParams({ email: email.toLowerCase() });
  // Concatenated rather than `new URL(path, base)`, which would discard any path
  // prefix on the base URL.
  const response = await fetch(`${BACKEND_API_URL}/borrow/application/admins?${query}`, {
    next: { revalidate: 60 * 30, tags: ["admins"] },
  });

  if (!response.ok) {
    throw new BackendError("GET_IS_ADMIN_FAILED", response.status, "Failed to fetch admin status");
  }

  return responseSchema.parse(await response.json()).isAdmin;
}
